import {ReactSession} from "react-client-session";
import {AES, enc} from "crypto-js";
import Header from "./Header";
import {useState, useEffect, Suspense} from "react";
import useSound from 'use-sound';
import emergSound from "../sound/emergency_tone.mp3"
import ApiRequester from "./ApiRequester";
import {
    durationToNow, sortFunc, setSoundsMute, isBannerPresent
} from "../libs";
import {ToastContainer} from "react-toastify";
import Footer from "./Footer";
import TableHeaders from "./component-assets/TableHeaders";
import CampusDropDown from "./component-assets/CampusDropDown";
import TableBody from "./component-assets/TableBody";
import Navigation from "./Navigation";
import SessionReload from "./component-assets/SessionReload";
import {Link} from "react-router-dom";
import Button from "./component-assets/Button";
import TableSkeleton from "./component-assets/skeletons/TableSkeleton";


const CurrentCloudAlerts = (props) => {

    let dData, headerData, csvHeaders;

    const [emergencySound] = useSound(emergSound, {volume: .5});

    headerData = [
        {label: "Event Start", key: "event_datetime_utc", align: "center", type: "datetime-utc",
            border: {enabled: true, borderType: "left", borderColorKey: "event_category", borderWidth: "6px"}},
        {label: "Duration", key: "event_datetime_utc", align: "center", type: "duration"},
        {label: "Alert Category / Type", key: ["event_category", "event_type"], align: "center", type: "stack"},
        {label: "Device / Serial", key: ["device_name", "manufacture_uuid"], align: "center",
            link: {enabled: true, type: "conditional-stack", conditionKey: "manufacture_id",
                condition: {
                    1: {linkto: "/profiles/inovonics", linkkey: "device_id"},
                    4: {linkto: "/profiles/palcare", linkkey: "device_id"}
                }}},
        {label: "Description", key: "device_description", align: "center"},
        {label: "Area", key: "area_name", align: "center",
            link: {enabled: true, type: "standard", linkto: "/profiles/area", linkkey: "area_id"}},
        {label: "Resident", key: "resident_name", align: "center",
            link: {enabled: true, type: "standard", linkto: "/profiles/resident", linkkey: "resident_id"}},
        {label: "Accepted By / Time", key: ["accepted_display", "accepted_datetime_utc"], align: "center",
            link: {type: "stack", enabled: true, linkto: "/profiles/user", linkkey: "accepted_user_id"}},
        {label: "Device Cleared", key: "ack_datetime_utc", align: "center", type: "datetime-utc"},
        {label: "Status", key: "status", type: "alert-status", align: "center",
            status: {className: {Waiting: "text-warning", Overdue: "text-danger", Accepted: "text-primary",
                                 Cleared: "text-primary", Arrived: "text-primary", Default: "text-primary"}},
            popover: {body: {Waiting: "Waiting for alert to be accepted or cleared if accept feature is N/A for device.",
                             Overdue: "Alert has been active over 20 minutes.",
                             Accepted: "Alert has been accepted by a staff member but the device is still in alarm state.",
                             Cleared: "Device has been cleared. If this device is listed for AFR in system settings, it must be accepted and finished through the mobile app.",
                             Arrived: "Staff has accepted the alert and the device's alarm state has been cleared. Alert must be finished to close out.",
                             Default: ""
                            }
                      }
        }
    ];

    csvHeaders = [
        {label: "Event Time", key: "event_datetime_utc"},
        {label: "Alert Category", key: "event_category"},
        {label: "Alert Type", key: "event_type"},
        {label: "Device Name", key: "device_name"},
        {label: "Device Description", key: "device_description"},
        {label: "Device Maker", key: "manufacture_name"},
        {label: "Device Unique ID", key: "manufacture_uuid"},
        {label: "Area", key: "area_name"},
        {label: "Resident First Name", key: "resident_first_name"},
        {label: "Resident Last Name", key: "resident_last_name"},
        {label: "Accepted User", key: "accepted_username"},
        {label: "Accepted User Display Name", key: "accepted_display"},
        {label: "Accepted Time", key: "accepted_datetime_utc"},
        {label: "Device Cleared Time", key: "ack_datetime_utc"},
        {label: "Vendor Invoked", key: "vendor"},
        {label: "Device Cleared Time", key: "ack_datetime_utc"}
    ];

    if (ReactSession.get("PAL") != null){
        let bData = AES.decrypt(ReactSession.get("PAL"), process.env.REACT_APP_ESECRET);
        dData = JSON.parse(bData.toString(enc.Utf8));
    }

    const [dataOrigin, setDataOrigin] = useState([]);
    const [originLoaded, setOriginLoaded] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [skeleton, setSkeleton] = useState(<TableSkeleton />);
    const [activeData, setActiveData] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [sorter, setSorter] = useState({
        sortColumn: "event_datetime_utc",
        sortOrder: "asc"
    });
    const [count, setCount] = useState(0);
    const [soundOff, setSoundOff] = useState(dData.soundsOff);
    const [soundInit, setSoundInit] = useState(true);
    const [alertKeyList, setAlertKeyList] = useState([]);
    const [campusId, setCampusId] = useState(dData.acnt);
    const [soundOn, setSoundOn] = useState(true);

    useEffect(() => {
        let interval;
        interval = null;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        if (count <= 0){
            ApiRequester({reqEndpoint: "get-alerts-cloud", CampusId: dData.acnt, ToGet: "emergency",
                                UserId: dData.user, Mode: props?.fullscreen ? "station" : "standard"}).then(data => {
                    setDataOrigin(data ? data : []);
                    setOriginLoaded(true);
            })
            setCount((count) => 7);
        }
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        // Performs realtime search of data as location types.
        let tmpData;
        tmpData = [];
        for (let i=0; i<dataOrigin.length; i++){
                tmpData.push(dataOrigin[i]);
        }
        setActiveData(tmpData);
    }, [dataOrigin]);

    useEffect(() => {
        if (![originLoaded].includes(false)){
            setPageLoaded(true);
        }
    }, [originLoaded]);

    useEffect(() => {
        let tmp = [], playSound = false, newKeys = [];
        for (let i=0; i < activeData?.length; i++){
            activeData[i].elapsed = durationToNow(activeData[i].event_date_time);
            tmp.push(activeData[i]);
            if (!alertKeyList.includes(activeData[i]?.event_id) && !newKeys.includes(activeData[i]?.event_id)){
                newKeys.push(activeData[i]?.event_id)
                if (!soundInit){
                    playSound = true;
                }
            }
        }
        if (newKeys.length > 0){
            setAlertKeyList((prevState) => {
                let items = [...prevState]
                for (let i=0; i < newKeys.length; i++){
                    items.push(newKeys[i])
                }
                return items
            })
        }
        if (tmp?.length > 0) {
            tmp.sort((a, b) => sortFunc(a, b, sorter.sortColumn, sorter.sortOrder));
        }
        setTableRows(<TableBody start={0} end={tmp?.length ? tmp.length : 0} tableData={tmp}
                                checkbox={false} writeAccess={false} unk={"a-"} checkidkey={"event_id"}
                                actionAppearance={() => {}} tableHeaders={headerData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        if (playSound && !soundOff){
            emergencySound();
        }
        if (soundInit){
            setSoundInit(false);
        }
    }, [activeData, sorter, count]);


    const tableHeaders = <TableHeaders checkbox={false} headerData={headerData} sortOrder={sorter.sortOrder}
                                       borderBuffer={true} sortColumn={sorter.sortColumn} sorter={sorter}
                                       setSorter={setSorter} />

    const headerContent = (
        <div className={"d-flex justify-content-between"}>
            {props?.fullscreen
                ?
                <div>
                    <Link to={`/emergency`}>
                        <Button text={"Exit Station Mode"} class={"btn btn-primary"} icon={true}
                                iconClass={"fe fe-minimize-2 me-2"}/>
                    </Link>
                </div>
                : <div>
                    <Link to={`/emergency/station`}>
                        <Button text={"Enter Station Mode"} class={"btn btn-primary"} icon={true}
                                iconClass={"fe fe-maximize-2 me-2"}/>
                    </Link>
                </div>
            }
            <CampusDropDown campusId={campusId}
                            setCampusId={(item) => setCampusId(item)}
                            setCount={(item) => setCount(item)}
                            pageAction={(e) => {
                                setOriginLoaded(false);
                                setPageLoaded(false);
                                if (dData?.campusData[campusId.toString()]?.is_cloud !== dData?.campusData[e.target.value]?.is_cloud){
                                    if (dData?.campusData[e.target.value]?.is_cloud){
                                        window.location.href = "/emergency";
                                    }else{
                                        window.location.href = "/alerts";
                                    }
                                }
                            }} />
        </div>
    );
    return (
        <>
            {dData?.migrated && <SessionReload />}
            {!props?.fullscreen && <Navigation/>}
            <div className={"main-content"}>
                <Header preTitle={"Alerts 5.0"} Title={"Emergency"} content={headerContent}/>
                <div className={"container-fluid"}>
                    {!dData?.campusData[campusId.toString()]?.soundsOff &&
                        <div className={"form-check mb-3"}>
                            <input className={"form-check-input table-checkbox"} type={"checkbox"} checked={soundOff}
                                   name={"sound-on-checkbox"} id={"sound-on-checkbox"}
                                   style={{width: "20px", height: "20px"}}
                                   onChange={(e) => {
                                       setSoundOff(e.target.checked);
                                       setSoundsMute(dData, e.target.checked);
                                   }}/>
                            <label className={"form-check-label"} htmlFor={"sound-on-checkbox"}>Mute Sounds</label>
                        </div>
                    }
                    <div className={"row"}>
                        {!pageLoaded
                            ?   skeleton
                            :   <table className={"table table-sm table-white table-hover"}>
                                    <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                    {tableHeaders}
                                    </thead>
                                    <tbody id={"tBody"}>
                                    {tableRows}
                                    </tbody>
                                </table>
                        }
                    </div>
                    <div className={"footer-spacer"}>

                    </div>
                </div>
                <Footer/>
                <Suspense fallback={"Loading..."}>
                    <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                    autoClose={10000} closeOnClick={true}/>
                </Suspense>
            </div>
        </>
    );
};

export default CurrentCloudAlerts;